import React from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Checkbox } from 'components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Alert, AlertDescription } from 'components/ui/alert';
import { AccessLevel, Permission } from './api';

interface RoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    role_name: string;
    access_level_id: number | null;
    permissions: Record<string, boolean>;
  }) => Promise<void>;
  availablePermissions: Permission[];
  accessLevels: AccessLevel[];
}

const RoleModal: React.FC<RoleModalProps> = ({ 
  open, 
  onOpenChange, 
  onSubmit, 
  availablePermissions,
  accessLevels 
}) => {
  const [roleName, setRoleName] = React.useState<string>('');
  const [accessLevelId, setAccessLevelId] = React.useState<string>('all');
  const [permissions, setPermissions] = React.useState<Record<string, boolean>>({});
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');

  React.useEffect(() => {
    if (open) {
      setRoleName('');
      setAccessLevelId('all');
      // Initialize all permissions to false
      const initialPermissions: Record<string, boolean> = {};
      availablePermissions.forEach(perm => {
        initialPermissions[perm.perm_key] = false;
      });
      setPermissions(initialPermissions);
      setError('');
    }
  }, [open, availablePermissions]);

  const handlePermissionToggle = (permKey: string) => {
    setPermissions(prev => ({
      ...prev,
      [permKey]: !prev[permKey]
    }));
  };

  const handleSubmit = async () => {
    if (!roleName.trim()) {
      setError('Role name is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await onSubmit({
        role_name: roleName.trim(),
        access_level_id: accessLevelId === 'all' ? null : Number(accessLevelId),
        permissions
      });
      onOpenChange(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Role</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Role Name */}
          <div className="space-y-2">
            <Label htmlFor="roleName">Role Name *</Label>
            <Input
              id="roleName"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              placeholder="Enter role name"
            />
          </div>

          {/* Access Level */}
          <div className="space-y-2">
            <Label htmlFor="accessLevel">Access Level *</Label>
            <Select value={accessLevelId} onValueChange={setAccessLevelId}>
              <SelectTrigger>
                <SelectValue placeholder="Select access level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {accessLevels.map((level) => (
                  <SelectItem key={level.id} value={String(level.id)}>
                    {level.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Permissions */}
          <div className="space-y-3">
            <Label>Permissions</Label>
            <div className="space-y-2 max-h-60 overflow-y-auto border rounded-md p-3">
              {availablePermissions.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  No permissions available
                </div>
              ) : (
                availablePermissions.map((permission) => (
                  <div
                    key={permission.perm_key}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {permission.perm_desc}
                      </span>
                    </div>
                    <Checkbox
                      checked={permissions[permission.perm_key] || false}
                      onCheckedChange={() => handlePermissionToggle(permission.perm_key)}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating...' : 'Create Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RoleModal;
